import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Image as ImageIcon, Film, Loader2, Globe, RefreshCw, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Admin page that lets the AMC team manage the public landing-page gallery
 * (photos + videos displayed under "Fotos e vídeos dos serviços realizados").
 */
export default function LandingMedia() {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [section, setSection] = useState("trabalhos");
  const [category, setCategory] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [featured, setFeatured] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rs] = await Promise.all([
        api.get("/landing/media?limit=500"),
        api.get("/schools").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(r.data) ? r.data : []);
      setSchools(Array.isArray(rs.data) ? rs.data : []);
    } catch (e) {
      toast.error("Não foi possível carregar as mídias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // --- Regenerate mobile mockups via background Playwright job ---
  const [regenJob, setRegenJob] = useState(null);
  const pollRef = useRef(null);
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/landing/mockups/regenerate-mobile/status", { silent: true });
        setRegenJob(r.data);
        if (r.data?.status === "done" || r.data?.status === "failed") {
          stopPolling();
          if (r.data.status === "done") {
            toast.success("Prints mobile atualizados!");
            load();
          } else {
            toast.error("Falha ao regenerar prints: " + (r.data.error || ""));
          }
        }
      } catch {}
    }, 2500);
  };
  useEffect(() => {
    // Resume polling if the server already has a job running from a previous session
    (async () => {
      try {
        const r = await api.get("/landing/mockups/regenerate-mobile/status", { silent: true });
        if (r.data?.status && r.data.status !== "idle") {
          setRegenJob(r.data);
          if (r.data.status === "running" || r.data.status === "queued") startPolling();
        }
      } catch {}
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const regenerateMobileMockups = async () => {
    if (!window.confirm(
      "Isso vai apagar todos os prints MOBILE atuais e gerar novos capturando o app no celular. Continuar?",
    )) return;
    try {
      const r = await api.post("/landing/mockups/regenerate-mobile");
      setRegenJob({ status: "queued", progress: { captured: 0, total: 0, current: "iniciando..." } });
      startPolling();
      toast.message("Regeneração iniciada — pode levar 1-2 minutos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível iniciar a regeneração");
    }
  };
  const regenRunning = regenJob && (regenJob.status === "running" || regenJob.status === "queued");

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("section", section);
      if (category) fd.append("category", category);
      if (schoolId) fd.append("school_id", schoolId);
      fd.append("featured", featured ? "true" : "false");
      try {
        await api.post("/landing/media", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`Falha em ${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setUploading(false);
    setCaption("");
    if (inputRef.current) inputRef.current.value = "";
    if (ok) toast.success(`${ok} arquivo${ok > 1 ? "s" : ""} enviado${ok > 1 ? "s" : ""}`);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta mídia da landing?")) return;
    try {
      await api.delete(`/landing/media/${id}`);
      toast.success("Removido");
      setItems((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      toast.error("Falha ao remover");
    }
  };

  const updateCaption = async (id, newCaption) => {
    try {
      await api.patch(`/landing/media/${id}`, { caption: newCaption });
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, caption: newCaption } : m)));
    } catch (e) {
      toast.error("Falha ao salvar legenda");
    }
  };

  const toggleFeatured = async (id, next) => {
    // Optimistic update
    setItems((prev) => prev.map((m) => (m.id === id ? { ...m, featured: next } : m)));
    try {
      await api.patch(`/landing/media/${id}`, { featured: next });
      toast.success(next ? "Destacado na home" : "Removido dos destaques");
    } catch (e) {
      // Rollback
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, featured: !next } : m)));
      toast.error("Falha ao atualizar destaque");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black text-[#1e3a8a]">
            Mídia da landing
          </h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
            <Globe className="w-4 h-4" />
            Fotos e vídeos exibidos publicamente em{" "}
            <span className="font-semibold text-[#7c3aed]">amcescolas.com</span>
          </p>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          data-testid="landing-media-preview"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Globe className="w-4 h-4" /> Abrir landing
        </a>
      </header>

      {/* Regenerate mobile mockups */}
      <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 text-white flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-heading font-black text-[#0c4a6e] text-sm">
              Prints mobile automáticos
            </p>
            <p className="text-[11px] text-sky-900/80 mt-0.5 leading-snug">
              Captura o app no celular (390×844), oculta valores R$, nomes de escolas, diretoras,
              e-mails/telefones, e substitui todos os prints mobile existentes.
            </p>
            {regenRunning && regenJob?.progress && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-[10px] text-sky-900 font-semibold mb-0.5">
                  <span>{regenJob.progress.current || "processando..."}</span>
                  <span>
                    {regenJob.progress.captured || 0}
                    {regenJob.progress.total ? ` / ${regenJob.progress.total}` : ""}
                  </span>
                </div>
                <div className="h-1.5 bg-sky-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-sky-600 transition-all"
                    style={{
                      width: regenJob.progress.total
                        ? `${Math.min(100, (regenJob.progress.captured / regenJob.progress.total) * 100)}%`
                        : "15%",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <Button
            onClick={regenerateMobileMockups}
            disabled={regenRunning}
            data-testid="btn-regen-mobile-mockups"
            className="bg-sky-700 hover:bg-sky-800 text-white"
          >
            {regenRunning ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Capturando…</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-1.5" />Trocar prints</>
            )}
          </Button>
        </div>
      </div>

      {/* Uploader */}
      <div className="rounded-2xl border-2 border-dashed border-[#7c3aed]/30 bg-violet-50/40 p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-[#7c3aed] text-white flex items-center justify-center">
            <Upload className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="font-heading font-black text-[#1e3a8a]">Adicionar foto ou vídeo</p>
            <p className="text-xs text-slate-600 mt-0.5">
              JPG, PNG, WEBP, MP4, WEBM ou MOV. Pode selecionar vários arquivos de uma vez.
            </p>

            {/* Step 1 — Visual guide */}
            <div className="mt-4 bg-white border border-amber-200 rounded-xl p-3">
              <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wide flex items-center gap-1">
                <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-black">1</span>
                Para onde essa foto vai?
              </p>
              <div className="mt-2 flex gap-2 flex-wrap">
                {[
                  { k: "trabalhos",     l: "Galeria pública",    hint: "Aparece em /galeria (botão 'Ver todos' na home)" },
                  { k: "portal_escola", l: "Mockup Portal",      hint: "Prints da tela da escola — aparecem na home" },
                  { k: "painel_admin",  l: "Mockup Admin",       hint: "Prints do painel administrativo — aparecem na home" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setSection(o.k)}
                    data-testid={`section-${o.k}`}
                    title={o.hint}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors text-left flex-1 min-w-[150px] ${
                      section === o.k
                        ? "bg-[#1e3a8a] text-white shadow"
                        : "bg-white text-[#1e3a8a] border border-[#1e3a8a]/15 hover:bg-[#1e3a8a]/5"
                    }`}
                  >
                    <div>{o.l}</div>
                    <div className={`text-[10px] mt-0.5 font-normal ${section === o.k ? "text-white/80" : "text-slate-500"}`}>
                      {o.hint}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 — Classification */}
            <div className="mt-3 bg-white border border-violet-200 rounded-xl p-3">
              <p className="text-[11px] font-bold text-violet-800 uppercase tracking-wide flex items-center gap-1">
                <span className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center text-[10px] font-black">2</span>
                Classificar e destacar
              </p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase tracking-wide">Categoria do serviço</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="media-category-select"
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    <option value="">— Sem categoria —</option>
                    <option value="pintura">Pintura</option>
                    <option value="drywall">Drywall / Gesso</option>
                    <option value="eletrica">Elétrica</option>
                    <option value="hidraulica">Hidráulica</option>
                    <option value="alvenaria">Alvenaria</option>
                    <option value="pedreiro">Pedreiro</option>
                    <option value="limpeza">Limpeza / Jardinagem</option>
                    <option value="mobiliario">Mobiliário</option>
                    <option value="cobertura">Cobertura / Telhado</option>
                    <option value="portas">Portas e Janelas</option>
                    <option value="piso">Piso</option>
                    <option value="marcenaria">Marcenaria</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase tracking-wide">Escola (opcional)</label>
                  <select
                    value={schoolId}
                    onChange={(e) => setSchoolId(e.target.value)}
                    data-testid="media-school-select"
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    <option value="">— Nenhuma —</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.nickname || s.name}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none h-9 px-3 bg-amber-50 border border-amber-300 rounded-md hover:bg-amber-100">
                  <input
                    type="checkbox"
                    checked={featured}
                    onChange={(e) => setFeatured(e.target.checked)}
                    data-testid="media-featured-check"
                    className="w-5 h-5 accent-amber-500"
                  />
                  <span className="text-xs font-bold text-amber-800">⭐ Destacar na home</span>
                </label>
              </div>
              <p className="text-[10px] text-slate-500 mt-2">
                <strong>⭐ Destacada na home</strong> = aparece na página inicial (`amcescolas.com`).
                <strong> Não destacada</strong> = fica apenas na galeria pública completa (/galeria).
              </p>
            </div>

            <div className="mt-3 bg-white border border-emerald-200 rounded-xl p-3">
              <label className="block text-[11px] font-bold text-emerald-800 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-black">3</span>
                Legenda e envio
              </label>
              <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Legenda opcional (ex.: Pintura do pátio — EMEB Caetano de Campos)"
                  data-testid="media-caption-input"
                />
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  hidden
                  data-testid="media-file-input"
                  onChange={(e) => onFiles(Array.from(e.target.files || []))}
                />
                <Button
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                  data-testid="media-upload-btn"
                >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Enviando</>
                ) : (
                  <><Upload className="w-4 h-4 mr-1" />Enviar arquivo(s)</>
                )}
              </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <ImageIcon className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-3 font-heading font-bold text-slate-700">Nenhuma mídia adicionada ainda</p>
          <p className="text-xs text-slate-500 mt-1">
            Envie fotos de serviços realizados ou vídeos curtos para exibir na landing.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {items.map((m) => (
            <MediaCard key={m.id} m={m} onUpdate={load} updateCaption={updateCaption} toggleFeatured={toggleFeatured} remove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Compact grid card. Shows a square thumbnail by default; video items also
 * expose a tiny "cortar" button that opens an inline trim editor allowing
 * the admin to pick the start/end seconds (saved via PATCH as
 * ``trim_start_sec`` / ``trim_end_sec`` — honored by the landing player).
 */
function MediaCard({ m, onUpdate, updateCaption, toggleFeatured, remove }) {
  const [trimOpen, setTrimOpen] = useState(false);
  return (
    <article
      className="group rounded-xl overflow-hidden border border-slate-100 bg-white shadow-sm hover:shadow-lg transition-shadow flex flex-col"
      data-testid={`media-item-${m.id}`}
    >
      <div className="relative bg-slate-100 aspect-square">
        {m.kind === "video" ? (
          <video
            src={`${API}/files/${m.storage_path}`}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={`${API}/files/${m.storage_path}`}
            alt={m.caption || "Mídia"}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/90 text-[9px] font-bold text-[#1e3a8a]">
          {m.kind === "video" ? <Film className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
          {m.kind === "video" ? "Vídeo" : "Imagem"}
        </span>
        <span className="absolute top-1.5 right-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-black/60 text-[9px] font-bold text-white">
          {m.section === "portal_escola" ? "Portal" : m.section === "painel_admin" ? "Admin" : "Trab."}
        </span>
      </div>
      <div className="p-2 flex flex-col gap-1.5">
        <Input
          defaultValue={m.caption || ""}
          placeholder="Legenda"
          className="text-[11px] h-8"
          data-testid={`caption-input-${m.id}`}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (m.caption || "")) updateCaption(m.id, v);
          }}
        />
        <div className="flex items-center justify-between gap-1">
          <button
            type="button"
            onClick={() => toggleFeatured(m.id, !m.featured)}
            data-testid={`toggle-featured-${m.id}`}
            className={`h-6 px-1.5 rounded-full text-[9px] font-bold transition-colors ${
              m.featured
                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
            title={m.featured ? "Destacado na home" : "Clique para destacar"}
          >
            {m.featured ? "⭐ Home" : "☆ Home"}
          </button>
          {(m.section === "portal_escola" || m.section === "painel_admin") && (
            <DeviceToggle m={m} />
          )}
          {m.kind === "video" && (
            <button
              type="button"
              onClick={() => setTrimOpen((v) => !v)}
              data-testid={`trim-toggle-${m.id}`}
              className={`h-6 px-1.5 rounded-full text-[9px] font-bold transition-colors ${
                m.trim_start_sec || m.trim_end_sec
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              title="Cortar trecho (início/fim)"
            >
              ✂ Cortar
            </button>
          )}
          <button
            onClick={() => remove(m.id)}
            data-testid={`media-delete-${m.id}`}
            className="h-6 px-1.5 text-red-600 hover:text-red-700"
            title="Remover"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
        {m.kind === "video" && trimOpen && (
          <VideoTrim m={m} onDone={onUpdate} />
        )}
      </div>
    </article>
  );
}

/**
 * Inline video-trim editor. The admin scrubs the preview to the desired
 * moment and clicks "Marcar início" / "Marcar fim" to capture the current
 * timestamp. The player on the landing page then auto-seeks to ``start``
 * and auto-pauses at ``end``.
 */
function VideoTrim({ m, onDone }) {
  const videoRef = useRef(null);
  const [start, setStart] = useState(m.trim_start_sec ?? "");
  const [end, setEnd] = useState(m.trim_end_sec ?? "");
  const [saving, setSaving] = useState(false);

  const fmt = (s) => {
    const n = Number(s);
    if (Number.isNaN(n)) return "";
    const mm = Math.floor(n / 60);
    const ss = Math.floor(n % 60).toString().padStart(2, "0");
    const dec = Math.round((n % 1) * 10);
    return `${mm}:${ss}.${dec}`;
  };

  const captureStart = () => {
    const v = videoRef.current;
    if (!v) return;
    setStart(Number(v.currentTime.toFixed(1)));
  };
  const captureEnd = () => {
    const v = videoRef.current;
    if (!v) return;
    setEnd(Number(v.currentTime.toFixed(1)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/landing/media/${m.id}`, {
        trim_start_sec: start === "" ? null : Number(start),
        trim_end_sec: end === "" ? null : Number(end),
      });
      toast.success("Trecho salvo");
      onDone?.();
    } catch (e) {
      toast.error("Não foi possível salvar o corte");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch(`/landing/media/${m.id}`, { trim_start_sec: null, trim_end_sec: null });
      setStart("");
      setEnd("");
      toast.success("Corte removido");
      onDone?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 p-2 rounded-lg bg-emerald-50 border border-emerald-200">
      <video
        ref={videoRef}
        src={`${API}/files/${m.storage_path}`}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded bg-black mb-2"
      />
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={captureStart}
          data-testid={`trim-set-start-${m.id}`}
          className="h-7 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold"
        >
          Marcar início
        </button>
        <button
          type="button"
          onClick={captureEnd}
          data-testid={`trim-set-end-${m.id}`}
          className="h-7 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold"
        >
          Marcar fim
        </button>
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5 text-[10px]">
        <div className="flex flex-col">
          <span className="text-emerald-800 font-semibold">Início {start !== "" && `(${fmt(start)})`}</span>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-7 text-[10px] mt-0.5"
            data-testid={`trim-start-input-${m.id}`}
            placeholder="segundos"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-rose-800 font-semibold">Fim {end !== "" && `(${fmt(end)})`}</span>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-7 text-[10px] mt-0.5"
            data-testid={`trim-end-input-${m.id}`}
            placeholder="segundos"
          />
        </div>
      </div>
      <div className="mt-2 flex gap-1.5">
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="flex-1 h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700"
          data-testid={`trim-save-${m.id}`}
        >
          Salvar
        </Button>
        <Button
          onClick={clear}
          disabled={saving}
          size="sm"
          variant="outline"
          className="h-7 text-[10px]"
          data-testid={`trim-clear-${m.id}`}
        >
          Limpar
        </Button>
      </div>
    </div>
  );
}

/**
 * Three-way toggle used on ``portal_escola`` and ``painel_admin`` media
 * cards: 📱 (mobile only), 💻 (desktop only) or 🔄 (both). The landing
 * page uses the stored ``device`` field to pick what to show on each
 * viewport, so the admin can keep a desktop + a mobile version of the
 * same mockup side by side.
 */
function DeviceToggle({ m }) {
  const [device, setDevice] = useState(m.device || "both");
  const cycle = async () => {
    const next = device === "both" ? "mobile" : device === "mobile" ? "desktop" : "both";
    setDevice(next);
    try {
      await api.patch(`/landing/media/${m.id}`, { device: next });
    } catch {
      setDevice(device); // rollback
      toast.error("Não foi possível alterar o dispositivo");
    }
  };
  const label = device === "mobile" ? "📱 Cel" : device === "desktop" ? "💻 PC" : "🔄 Ambos";
  const tint =
    device === "mobile"
      ? "bg-sky-100 text-sky-800"
      : device === "desktop"
      ? "bg-violet-100 text-violet-800"
      : "bg-slate-100 text-slate-600";
  return (
    <button
      type="button"
      onClick={cycle}
      className={`h-6 px-1.5 rounded-full text-[9px] font-bold transition-colors ${tint}`}
      data-testid={`device-toggle-${m.id}`}
      title="Alternar: Celular / Desktop / Ambos"
    >
      {label}
    </button>
  );
}
